import React, { useEffect, useMemo, useReducer, useRef, useState } from "react";
import { DownloadFile, HttpNoData } from "../../Core.js";
import styles from "./ExcelExport.module.css";

/**
 * Ожидаемые поля у worker:
 * { id, name, workerCategoryId, softDeleted }
 * Ожидаемые поля у category:
 * { id, name }
 */

const initialState = {
  jwt: "",
  // отчеты
  reports: {
    isCategoryPointsDetail: false,
    isCategoryHoursDetail: false,
    isAnalytic: false,
    isCoefficient: false,
    isSends: false,
    isNewSends: false,
    isDefect: false,
    isStable: false,
    isLeads: false,
    isMachineMath: false,
    isExtraWork: false,
    isProductWorkDetailed: false,
    isBudgets: false,
    isKpdSnapshots: false,
    isNormsSnapshots: false,
  },

  // период
  period: {
    periodType: "month", // month | year | custom
    dateMonth: "", // YYYY-MM
    year: new Date().getFullYear(),
    startDate: "",
    endDate: "",
  },

  // данные
  workers: [], // [{...worker, isCheck}]
  categories: [], // [{...cat}]
  ready: false,
  downloading: false,
  error: null,
};

function reducer(state, action) {
  switch (action.type) {
    case "INIT": {
      return { ...state, jwt: action.jwt };
    }

    case "SET_REPORT": {
      const { key, value } = action;
      return { ...state, reports: { ...state.reports, [key]: value } };
    }

    case "SET_ALL_REPORTS": {
      const value = action.value;
      const next = {};
      for (const k of Object.keys(state.reports)) next[k] = value;
      return { ...state, reports: next };
    }

    case "SET_PERIOD_TYPE": {
      return {
        ...state,
        period: { ...state.period, periodType: action.value },
      };
    }

    case "SET_PERIOD_FIELD": {
      const { key, value } = action;
      return {
        ...state,
        period: { ...state.period, [key]: value },
      };
    }

    case "SET_DATA": {
      const { workers, categories } = action;

      return {
        ...state,
        workers: workers.map((w) => ({ ...w, isCheck: !!w.isCheck })),
        categories: categories,
        ready: true,
        error: null,
      };
    }

    case "SET_READY": {
      return { ...state, ready: action.value };
    }

    case "SET_DOWNLOADING": {
      return { ...state, downloading: action.value };
    }

    case "SET_ERROR": {
      return { ...state, error: action.error || "Ошибка" };
    }

    case "TOGGLE_WORKER": {
      const { id, value } = action;
      return {
        ...state,
        workers: state.workers.map((w) => (w.id === id ? { ...w, isCheck: value } : w)),
      };
    }

    case "TOGGLE_WORKERS_BY_FILTER": {
      // меняем чек у тех, кто проходит фильтр
      const { predicate, value } = action;
      return {
        ...state,
        workers: state.workers.map((w) => (predicate(w) ? { ...w, isCheck: value } : w)),
      };
    }

    case "TOGGLE_ALL": {
      const value = action.value;
      return {
        ...state,
        workers: state.workers.map((w) => ({ ...w, isCheck: value })),
      };
    }

    default:
      return state;
  }
}

function formatIsoDate(d) {
  return d.toISOString().split("T")[0];
}
function formatIsoMonth(d) {
  // YYYY-MM
  return d.toISOString().split("T")[0].substring(0, 7);
}

function IndeterminateCheckbox({ checked, indeterminate, onChange, className, disabled }) {
  const ref = useRef(null);

  useEffect(() => {
    if (ref.current) ref.current.indeterminate = !!indeterminate && !checked;
  }, [indeterminate, checked]);

  return (
    <input
      ref={ref}
      type="checkbox"
      className={className}
      checked={!!checked}
      disabled={disabled}
      onChange={(e) => onChange(e.target.checked)}
    />
  );
}

function Section({ title, right, children }) {
  return (
    <section className={styles.section}>
      <div className={styles.sectionHead}>
        <h3 className={styles.sectionTitle}>{title}</h3>
        {right ? <div className={styles.sectionRight}>{right}</div> : null}
      </div>
      <div className={styles.sectionBody}>{children}</div>
    </section>
  );
}

function Row({ children, onClick, className }) {
  return (
    <div className={`${styles.row} ${className || ""}`} onClick={onClick} role={onClick ? "button" : undefined} tabIndex={onClick ? 0 : undefined}>
      {children}
    </div>
  );
}

export default function ExcelExport(props) {
  const [state, dispatch] = useReducer(reducer, { ...initialState, jwt: props.jwt || "" });
  const [search, setSearch] = useState("");

  // init period defaults
  useEffect(() => {
    dispatch({ type: "INIT", jwt: props.jwt || "" });

    const today = new Date();
    const firstDayOfMonth = new Date(today.getFullYear(), today.getMonth(), 1);

    dispatch({ type: "SET_PERIOD_FIELD", key: "dateMonth", value: formatIsoMonth(today) });
    dispatch({ type: "SET_PERIOD_FIELD", key: "year", value: today.getFullYear() });
    dispatch({ type: "SET_PERIOD_FIELD", key: "startDate", value: formatIsoDate(firstDayOfMonth) });
    dispatch({ type: "SET_PERIOD_FIELD", key: "endDate", value: formatIsoDate(today) });
  }, [props.jwt]);

  // fetch data
  useEffect(() => {
    let alive = true;

    async function load() {
      try {
        dispatch({ type: "SET_READY", value: false });

        const [workersResp, catsResp] = await Promise.all([
          HttpNoData("/api/v0/workers/withDeleted", "GET", state.jwt),
          HttpNoData("/api/v0/workerCategories", "GET", state.jwt),
        ]);

        if (!alive) return;

        if (!workersResp.statusSuccessful) {
          dispatch({ type: "SET_ERROR", error: workersResp.error || "Не удалось загрузить сотрудников" });
          return;
        }
        if (!catsResp.statusSuccessful) {
          dispatch({ type: "SET_ERROR", error: catsResp.error || "Не удалось загрузить категории" });
          return;
        }

        const workers = (workersResp.data || []).map((w) => ({ ...w, isCheck: false }));
        const categories = (catsResp.data || []).map((c) => ({ ...c }));

        dispatch({ type: "SET_DATA", workers, categories });
      } catch (e) {
        if (!alive) return;
        dispatch({ type: "SET_ERROR", error: e?.message || "Ошибка загрузки" });
      }
    }

    if (state.jwt) load();
    return () => {
      alive = false;
    };
  }, [state.jwt]);

  const reportsSelectedCount = useMemo(() => {
    return Object.values(state.reports).filter(Boolean).length;
  }, [state.reports]);

  const workersSelectedCount = useMemo(() => {
    return state.workers.filter((w) => w.isCheck).length;
  }, [state.workers]);

  const allSelected = useMemo(() => {
    return reportsSelectedCount > 0 || workersSelectedCount > 0;
  }, [reportsSelectedCount, workersSelectedCount]);

  const filteredWorkers = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return state.workers;
    return state.workers.filter((w) => String(w.name || "").toLowerCase().includes(q));
  }, [state.workers, search]);

  const deletedWorkers = useMemo(() => state.workers.filter((w) => w.softDeleted === true), [state.workers]);
  const deletedSelectedCount = useMemo(() => deletedWorkers.filter((w) => w.isCheck).length, [deletedWorkers]);

  const categoryStats = useMemo(() => {
    const map = new Map();
    for (const c of state.categories) {
      const list = state.workers.filter((w) => w.workerCategoryId == c.id && w.softDeleted !== true);
      const checked = list.filter((w) => w.isCheck).length;
      map.set(c.id, { total: list.length, checked, list });
    }
    return map;
  }, [state.categories, state.workers]);

  const payload = useMemo(() => {
    const p = state.period;
    const periodType = p.periodType || "month";

    return {
      isAnalytic: !!state.reports.isAnalytic,
      isCoefficient: !!state.reports.isCoefficient,
      isSends: !!state.reports.isSends,
      isNewSends: !!state.reports.isNewSends,
      isDefect: !!state.reports.isDefect,
      isStable: !!state.reports.isStable,
      isLeads: !!state.reports.isLeads,
      isMachineMath: !!state.reports.isMachineMath,
      isExtraWork: !!state.reports.isExtraWork,
      isProductWorkDetailed: !!state.reports.isProductWorkDetailed,
      isCategoryPointsDetail: !!state.reports.isCategoryPointsDetail,
      isCategoryHoursDetail: !!state.reports.isCategoryHoursDetail,
      isBudgets: !!state.reports.isBudgets,
      isKpdSnapshots: !!state.reports.isKpdSnapshots,
      isNormsSnapshots: !!state.reports.isNormsSnapshots,

      periodType,
      date: periodType === "month" ? `${p.dateMonth || formatIsoMonth(new Date())}-01` : null,
      year: periodType === "year" ? (p.year || new Date().getFullYear()) : null,
      startDate: periodType === "custom" ? (p.startDate || formatIsoDate(new Date())) : null,
      endDate: periodType === "custom" ? (p.endDate || formatIsoDate(new Date())) : null,

      workers: state.workers.filter((w) => w.isCheck).map((w) => w.id),
      isLastMonth: false,
    };
  }, [state.period, state.reports, state.workers]);

  const canDownload = state.ready && !state.downloading;

  async function onDownload() {
    try {
      dispatch({ type: "SET_DOWNLOADING", value: true });

      const fileName = `${new Date().toJSON().replaceAll(":", "_").replaceAll("-", "_").replaceAll(".", "_")}.xlsx`;

      const resp = await DownloadFile("/api/v1/excel", "POST", state.jwt, payload, fileName);

      if (!resp.statusSuccessful) {
        dispatch({ type: "SET_ERROR", error: resp.error || "Ошибка скачивания" });
      }
    } catch (e) {
      dispatch({ type: "SET_ERROR", error: e?.message || "Ошибка скачивания" });
    } finally {
      dispatch({ type: "SET_DOWNLOADING", value: false });
    }
  }

  function toggleAll(value) {
    dispatch({ type: "SET_ALL_REPORTS", value });
    dispatch({ type: "TOGGLE_ALL", value });
  }

  return (
    <div className={styles.page}>
      <div className={styles.shell}>
        <header className={styles.header}>
          <div>
            <h1 className={styles.h1}>Экспорт Excel</h1>
            <div className={styles.sub}>
              Выберите отчеты, период и сотрудников. Затем скачайте файл.
            </div>
          </div>

          <div className={styles.headerMeta}>
            <div className={styles.kpi}>
              <div className={styles.kpiItem}>
                <div className={styles.kpiLabel}>Отчеты</div>
                <div className={styles.kpiValue}>{reportsSelectedCount}</div>
              </div>
              <div className={styles.kpiItem}>
                <div className={styles.kpiLabel}>Сотрудники</div>
                <div className={styles.kpiValue}>{workersSelectedCount}</div>
              </div>
            </div>
          </div>
        </header>

        {state.error ? <div className={styles.alert}>{state.error}</div> : null}

        <div className={styles.grid}>
          <div className={styles.col}>
            <Section
              title="Общее"
              right={
                <div className={styles.inline}>
                  <IndeterminateCheckbox
                    className={styles.checkbox}
                    checked={allSelected && reportsSelectedCount === Object.keys(state.reports).length && workersSelectedCount === state.workers.length}
                    indeterminate={allSelected}
                    onChange={(v) => toggleAll(v)}
                    disabled={!state.ready || state.downloading}
                  />
                  <span className={styles.inlineText}>Выбрать всё</span>
                </div>
              }
            >
              <div className={styles.stack}>
                <label className={styles.item}>
                  <input
                    className={styles.checkbox}
                    type="checkbox"
                    checked={state.reports.isAnalytic}
                    onChange={(e) => dispatch({ type: "SET_REPORT", key: "isAnalytic", value: e.target.checked })}
                    disabled={!state.ready || state.downloading}
                  />
                  <span>Статистика</span>
                </label>

                <label className={styles.item}>
                  <input
                    className={styles.checkbox}
                    type="checkbox"
                    checked={state.reports.isCoefficient}
                    onChange={(e) => dispatch({ type: "SET_REPORT", key: "isCoefficient", value: e.target.checked })}
                    disabled={!state.ready || state.downloading}
                  />
                  <span>Список работ</span>
                </label>

                <label className={styles.item}>
                  <input
                    className={styles.checkbox}
                    type="checkbox"
                    checked={state.reports.isSends}
                    onChange={(e) => dispatch({ type: "SET_REPORT", key: "isSends", value: e.target.checked })}
                    disabled={!state.ready || state.downloading}
                  />
                  <span>Отправленная продукция</span>
                </label>

                <label className={styles.item}>
                  <input
                    className={styles.checkbox}
                    type="checkbox"
                    checked={state.reports.isNewSends}
                    onChange={(e) => dispatch({ type: "SET_REPORT", key: "isNewSends", value: e.target.checked })}
                    disabled={!state.ready || state.downloading}
                  />
                  <span>Отправленная продукция (2026)</span>
                </label>
                <label className={styles.item}>
                  <input
                    className={styles.checkbox}
                    type="checkbox"
                    checked={state.reports.isProductWorkDetailed}
                    onChange={(e) => dispatch({ type: "SET_REPORT", key: "isProductWorkDetailed", value: e.target.checked })}
                    disabled={!state.ready || state.downloading}
                  />
                  <span>Детализация по работам и направлениям</span>
                </label>
                <label className={styles.item}>
                  <input
                    className={styles.checkbox}
                    type="checkbox"
                    checked={state.reports.isCategoryPointsDetail}
                    onChange={(e) => dispatch({ type: "SET_REPORT", key: "isCategoryPointsDetail", value: e.target.checked })}
                    disabled={!state.ready || state.downloading}
                  />
                  <span>Работы цеха по категориям в баллах</span>
                </label>
                <label className={styles.item}>
                  <input
                    className={styles.checkbox}
                    type="checkbox"
                    checked={state.reports.isCategoryHoursDetail}
                    onChange={(e) => dispatch({ type: "SET_REPORT", key: "isCategoryHoursDetail", value: e.target.checked })}
                    disabled={!state.ready || state.downloading}
                  />
                  <span>Работы цеха по категориям в часах</span>
                </label>

                <label className={styles.item}>
                  <input
                    className={styles.checkbox}
                    type="checkbox"
                    checked={state.reports.isDefect}
                    onChange={(e) => dispatch({ type: "SET_REPORT", key: "isDefect", value: e.target.checked })}
                    disabled={!state.ready || state.downloading}
                  />
                  <span>Дефекты</span>
                </label>

                <label className={styles.item}>
                  <input
                    className={styles.checkbox}
                    type="checkbox"
                    checked={state.reports.isStable}
                    onChange={(e) => dispatch({ type: "SET_REPORT", key: "isStable", value: e.target.checked })}
                    disabled={!state.ready || state.downloading}
                  />
                  <span>Рентабельность</span>
                </label>

                <label className={styles.item}>
                  <input
                    className={styles.checkbox}
                    type="checkbox"
                    checked={state.reports.isLeads}
                    onChange={(e) => dispatch({ type: "SET_REPORT", key: "isLeads", value: e.target.checked })}
                    disabled={!state.ready || state.downloading}
                  />
                  <span>Лиды</span>
                </label>

                <label className={styles.item}>
                  <input
                    className={styles.checkbox}
                    type="checkbox"
                    checked={state.reports.isMachineMath}
                    onChange={(e) => dispatch({ type: "SET_REPORT", key: "isMachineMath", value: e.target.checked })}
                    disabled={!state.ready || state.downloading}
                  />
                  <span>Учитывать лазерные ст.</span>
                </label>

                <label className={styles.item}>
                  <input
                    className={styles.checkbox}
                    type="checkbox"
                    checked={state.reports.isExtraWork}
                    onChange={(e) => dispatch({ type: "SET_REPORT", key: "isExtraWork", value: e.target.checked })}
                    disabled={!state.ready || state.downloading}
                  />
                  <span>Суммы работ</span>
                </label>
                <label className={styles.item}>
                  <input
                    className={styles.checkbox}
                    type="checkbox"
                    checked={state.reports.isBudgets}
                    onChange={(e) => dispatch({ type: "SET_REPORT", key: "isBudgets", value: e.target.checked })}
                    disabled={!state.ready || state.downloading}
                  />
                  <span>Бюджеты</span>
                </label>
                <label className={styles.item}>
                  <input
                    className={styles.checkbox}
                    type="checkbox"
                    checked={state.reports.isKpdSnapshots}
                    onChange={(e) => dispatch({ type: "SET_REPORT", key: "isKpdSnapshots", value: e.target.checked })}
                    disabled={!state.ready || state.downloading}
                  />
                  <span>КПД сотрудников по месяцам (с 2026 года)</span>
                </label>
                <label className={styles.item}>
                  <input
                    className={styles.checkbox}
                    type="checkbox"
                    checked={state.reports.isNormsSnapshots}
                    onChange={(e) => dispatch({ type: "SET_REPORT", key: "isNormsSnapshots", value: e.target.checked })}
                    disabled={!state.ready || state.downloading}
                  />
                  <span>Нормативы работ по месяцам (с 2026 года)</span>
                </label>
              </div>

              <div className={styles.hr} />

              <div className={styles.period}>
                <div className={styles.periodHead}>Период</div>

                <select
                  className={styles.select}
                  value={state.period.periodType}
                  onChange={(e) => dispatch({ type: "SET_PERIOD_TYPE", value: e.target.value })}
                  disabled={!state.ready || state.downloading}
                >
                  <option value="month">За месяц</option>
                  <option value="year">За год</option>
                  <option value="custom">Произвольный период</option>
                </select>

                {state.period.periodType === "month" ? (
                  <input
                    className={styles.input}
                    type="month"
                    value={state.period.dateMonth}
                    onChange={(e) => dispatch({ type: "SET_PERIOD_FIELD", key: "dateMonth", value: e.target.value })}
                    disabled={!state.ready || state.downloading}
                  />
                ) : null}

                {state.period.periodType === "year" ? (
                  <input
                    className={styles.input}
                    type="number"
                    min="2000"
                    max={new Date().getFullYear()}
                    value={state.period.year}
                    onChange={(e) => dispatch({ type: "SET_PERIOD_FIELD", key: "year", value: parseInt(e.target.value || "0", 10) })}
                    disabled={!state.ready || state.downloading}
                  />
                ) : null}

                {state.period.periodType === "custom" ? (
                  <div className={styles.range}>
                    <input
                      className={styles.input}
                      type="date"
                      value={state.period.startDate}
                      onChange={(e) => dispatch({ type: "SET_PERIOD_FIELD", key: "startDate", value: e.target.value })}
                      disabled={!state.ready || state.downloading}
                    />
                    <span className={styles.rangeSep}>—</span>
                    <input
                      className={styles.input}
                      type="date"
                      value={state.period.endDate}
                      onChange={(e) => dispatch({ type: "SET_PERIOD_FIELD", key: "endDate", value: e.target.value })}
                      disabled={!state.ready || state.downloading}
                    />
                  </div>
                ) : null}
              </div>
            </Section>
          </div>

          <div className={styles.colWide}>
            <Section
              title="Сотрудники"
              right={
                <div className={styles.tools}>
                  <input
                    className={styles.search}
                    placeholder="Поиск по имени…"
                    value={search}
                    onChange={(e) => setSearch(e.target.value)}
                    disabled={!state.ready || state.downloading}
                  />
                  <button
                    className={styles.ghost}
                    onClick={() => dispatch({ type: "TOGGLE_WORKERS_BY_FILTER", predicate: () => true, value: true })}
                    disabled={!state.ready || state.downloading}
                    type="button"
                  >
                    Выбрать всех
                  </button>
                  <button
                    className={styles.ghost}
                    onClick={() => dispatch({ type: "TOGGLE_WORKERS_BY_FILTER", predicate: () => true, value: false })}
                    disabled={!state.ready || state.downloading}
                    type="button"
                  >
                    Снять всех
                  </button>
                </div>
              }
            >
              <div className={styles.categories}>
                {state.categories.map((c) => {
                  const st = categoryStats.get(c.id) || { total: 0, checked: 0, list: [] };
                  if (st.total === 0) return null;

                  const checked = st.checked === st.total && st.total > 0;
                  const ind = st.checked > 0 && st.checked < st.total;

                  return (
                    <div key={c.id} className={styles.categoryCard}>
                      <div className={styles.categoryHead}>
                        <label className={styles.item}>
                          <IndeterminateCheckbox
                            className={styles.checkbox}
                            checked={checked}
                            indeterminate={ind}
                            disabled={!state.ready || state.downloading}
                            onChange={(v) =>
                              dispatch({
                                type: "TOGGLE_WORKERS_BY_FILTER",
                                predicate: (w) => w.workerCategoryId == c.id && w.softDeleted !== true,
                                value: v,
                              })
                            }
                          />
                          <span className={styles.categoryName}>{c.name}</span>
                        </label>

                        <div className={styles.badge}>
                          {st.checked}/{st.total}
                        </div>
                      </div>

                      <div className={styles.categoryBody}>
                        {st.list
                          .filter((w) => {
                            const q = search.trim().toLowerCase();
                            if (!q) return true;
                            return String(w.name || "").toLowerCase().includes(q);
                          })
                          .map((w) => (
                            <Row key={w.id} onClick={() => dispatch({ type: "TOGGLE_WORKER", id: w.id, value: !w.isCheck })}>
                              <input
                                className={styles.checkbox}
                                type="checkbox"
                                checked={w.isCheck}
                                onChange={(e) => dispatch({ type: "TOGGLE_WORKER", id: w.id, value: e.target.checked })}
                                disabled={!state.ready || state.downloading}
                              />
                              <div className={styles.workerName}>{w.name}</div>
                            </Row>
                          ))}
                      </div>
                    </div>
                  );
                })}

                {deletedWorkers.length ? (
                  <div className={`${styles.categoryCard} ${styles.deletedCard}`}>
                    <div className={styles.categoryHead}>
                      <label className={styles.item}>
                        <IndeterminateCheckbox
                          className={styles.checkbox}
                          checked={deletedSelectedCount === deletedWorkers.length && deletedWorkers.length > 0}
                          indeterminate={deletedSelectedCount > 0 && deletedSelectedCount < deletedWorkers.length}
                          disabled={!state.ready || state.downloading}
                          onChange={(v) =>
                            dispatch({
                              type: "TOGGLE_WORKERS_BY_FILTER",
                              predicate: (w) => w.softDeleted === true,
                              value: v,
                            })
                          }
                        />
                        <span className={styles.categoryName}>Удалённые</span>
                      </label>

                      <div className={styles.badge}>
                        {deletedSelectedCount}/{deletedWorkers.length}
                      </div>
                    </div>

                    <div className={styles.categoryBody}>
                      {deletedWorkers
                        .filter((w) => {
                          const q = search.trim().toLowerCase();
                          if (!q) return true;
                          return String(w.name || "").toLowerCase().includes(q);
                        })
                        .map((w) => (
                          <Row key={w.id} onClick={() => dispatch({ type: "TOGGLE_WORKER", id: w.id, value: !w.isCheck })}>
                            <input
                              className={styles.checkbox}
                              type="checkbox"
                              checked={w.isCheck}
                              onChange={(e) => dispatch({ type: "TOGGLE_WORKER", id: w.id, value: e.target.checked })}
                              disabled={!state.ready || state.downloading}
                            />
                            <div className={styles.workerName}>{w.name}</div>
                          </Row>
                        ))}
                    </div>
                  </div>
                ) : null}
              </div>

              {!state.categories.length && state.ready ? (
                <div className={styles.muted}>Категории не найдены.</div>
              ) : null}
            </Section>
          </div>
        </div>

        <footer className={styles.footer}>
          <div className={styles.footerLeft}>
            <div className={styles.smallMuted}>
              Период:{" "}
              {state.period.periodType === "month"
                ? state.period.dateMonth
                : state.period.periodType === "year"
                  ? state.period.year
                  : `${state.period.startDate} — ${state.period.endDate}`}
            </div>
            <div className={styles.smallMuted}>Выбрано: отчеты {reportsSelectedCount}, сотрудники {workersSelectedCount}</div>
          </div>

          <button className={styles.primary} disabled={!canDownload} onClick={onDownload} type="button">
            {state.downloading ? "Подождите…" : "Скачать Excel"}
          </button>
        </footer>
      </div>
    </div>
  );
}
